import { PayloadAction, createSlice } from '@reduxjs/toolkit';
import {
  IImageZoomDTO,
  IWellImageDTO,
  IWellImageParams,
  IWellImagingDetails,
} from 'business/models/Models';
import { ImageTuningValues } from 'business/models/ImageTuningModel';
import TransformPoints from 'components/plateviewer/well/TransformPoints';
import {
  DEFAULT_DRAWING_COLOR,
  DEFAULT_FONT_FAMILY,
  DEFAULT_FONT_SIZE,
} from 'components/well/constants';
import {
  AnnotationDrawing,
  AnnotationUpdateDetails,
  CrystalDrawing,
  DrawingKey,
  DrawingType,
  DrawingUpdateDetails,
  DrawingUpdateRequestParams,
  DropLocationDrawing,
  LightPathDrawing,
  OverlayTools,
  POIDrawing,
  ROIDrawing,
  RoiUpdateDetails,
  RulerDrawing,
  ScribbleDrawing,
  SelectedDrawingDetails,
} from 'components/well/drawingManager/constants';
import WellDropImageStore from 'components/well/wellDropImageStore';
import { RGBColor } from 'constants/';
import { EFetchingStatus } from 'enums/EFetchingStatus';
import {
  deleteDrawing,
  fetchDrawingPreferences,
  fetchDrawingsThunk,
  fetchDropImageThunk,
  saveDrawingThunk,
  toggleRoiImaging,
  fetchImagingDetails,
  updateDrawingThunk,
  fetchWellImagesBulkAction,
  fetchWellDropImageMetaDataThunk,
  saveWellDropTuningValuesThunk,
} from 'redux/thunks/well';
import {
  DrawingsFrame,
  getDrawingsFrame,
  getWellDropImageUniqueKey,
  getWellImagingDetailsKey,
  isDrawingsFrameStale,
} from 'redux/utils/well';
import { isObjectEmpty } from 'utils/Global';
import { TFocusLevelImage } from 'business/models/CustomModels';
import ECompareMode from 'enums/ECompareMode';
import { DEFAULT_OVERLAY_OPACITY } from 'components/well/utils';

export type DrawingsData = {
  annotations: AnnotationDrawing[];
  pois: POIDrawing[];
  crystals: CrystalDrawing[];
  rois: ROIDrawing[];
  rulers: RulerDrawing[];
  scribbles: ScribbleDrawing[];
  lightPaths: LightPathDrawing[];
  dropLocations: DropLocationDrawing[];
};

export interface WellImagingDetails extends IWellImagingDetails {
  status: EFetchingStatus;
}

export type WellImagingDetailsData = {
  [key: string]: WellImagingDetails;
};

export type Images = {
  [key: number]: {
    [key: number]: {
      status: EFetchingStatus;
      image: IWellImageDTO;
    };
  };
};

export type Drawings = {
  [key: number]: {
    drawings: DrawingsData;
    canvasSize: DrawingsFrame;
    status: EFetchingStatus;
  };
};

export type DropData = {
  [key: number]: {
    [key: number]: {
      [key: string]: {
        drawingsDetails: Drawings;
        images: Images;
        imagingDetails: WellImagingDetailsData;
        isImagingEnabled: boolean;
      };
    };
  };
};

export type WellDropImageMetaData = {
  status: EFetchingStatus;
  data: Record<string, IWellImageDTO>;
  error: string;
  fetchedMetaDataInspectionIds: Record<number, EFetchingStatus>; //  to keep track aginst which inspection we have fetched the metadata already, so we don't fetch again
};

const drawingsInitialState: DropData = {};

type DrawingFontsPreference = {
  fontFamily: string;
  fontSize: number;
};

export type StoreImage = {
  key: string; // key combine of plateId, wellNumber, dropNumber, batchId, cpID  - ref:getWellDropImageUniqueKey() function
  blobUrl: string;
  fetchingStatus: EFetchingStatus;
  meta: { updatedTuningValues?: ImageTuningValues | null };
};

export type StoreImages = {
  [key: string]: StoreImage; // key combine of plateId, wellNumber, dropNumber, batchId, cpID  - ref:getWellDropImageUniqueKey() function
};

type WellDropCompare = {
  compareMode: ECompareMode | undefined;
  overlayOpacity: number | undefined;
  displayOpacityBar: boolean;
  lockPan: boolean;
};

type WellStates = {
  wellDrawing: DropData;
  selectedDrawing: SelectedDrawingDetails;
  selectedDrawingTool: OverlayTools;
  drawingFontsPreferences: DrawingFontsPreference;
  drawingColor: RGBColor | string;
  poiColor: string;
  selectedWellDropDetailsIndex: number;
  imageStore: StoreImages;
  showOverrideTuning: boolean;
  showFrapGraph: boolean;
  FocusLevelImagesAvailableKeys: string[];
  wellsIndependentZoom: {
    [key: string]: boolean;
  };
  showScaleBar: boolean;
  showOverlays: boolean;
  showWellDropInfoPanel: boolean;
  isCreatingDrawing: boolean;
  imageZoomDto?: IImageZoomDTO;
  wellDropImageMetaData: WellDropImageMetaData;
  wellDropCompare?: WellDropCompare;
};

const initialWellDropImageMetaDataValues: WellDropImageMetaData = {
  data: {},
  status: EFetchingStatus.NotAvailable,
  error: '',
  fetchedMetaDataInspectionIds: {},
};

const initialState: WellStates = {
  wellDrawing: drawingsInitialState,
  selectedDrawing: null,
  selectedDrawingTool: OverlayTools.POINTER,
  drawingFontsPreferences: {
    fontFamily: DEFAULT_FONT_FAMILY,
    fontSize: DEFAULT_FONT_SIZE,
  },
  drawingColor: DEFAULT_DRAWING_COLOR,
  poiColor: '#FF0000',
  selectedWellDropDetailsIndex: 0,
  imageStore: {}, // this state is used for experiment and snf, since they can't co-exist in same state
  showOverrideTuning: true,
  showFrapGraph: false,
  FocusLevelImagesAvailableKeys: [],
  wellsIndependentZoom: {},
  showScaleBar: true,
  showOverlays: true,
  showWellDropInfoPanel: true,
  isCreatingDrawing: false,
  imageZoomDto: null,
  wellDropImageMetaData: initialWellDropImageMetaDataValues,
  wellDropCompare: {
    compareMode: ECompareMode.Grid,
    overlayOpacity: DEFAULT_OVERLAY_OPACITY,
    displayOpacityBar: false,
    lockPan: true,
  },
};
const WellSlice = createSlice({
  name: 'well',
  initialState,
  extraReducers: (builder) => {
    builder.addCase(fetchDrawingsThunk.pending, (state, action) => {
      const drawings = state.wellDrawing;
      const { plateId, wellNumber, dropNumber, regionName } = action.meta.arg;
      state.wellDrawing = {
        ...drawings,
        [plateId]: {
          ...drawings[plateId],
          [wellNumber]: {
            ...drawings[plateId]?.[wellNumber],
            [dropNumber]: {
              ...drawings[plateId]?.[wellNumber]?.[dropNumber],
              drawingsDetails: {
                ...drawings[plateId]?.[wellNumber]?.[dropNumber]?.drawingsDetails,
                [regionName]: {
                  drawings: {},
                },
              },
            },
          },
        },
      };
    });
    builder.addCase(fetchDrawingsThunk.fulfilled, (state, action) => {
      const drawings = state.wellDrawing;
      const { plateId, wellNumber, dropNumber, tpObject, regionName } = action.meta.arg;
      const { imagingEnabled, ...wellDrawings } = action.payload;
      state.wellDrawing = {
        ...drawings,
        [plateId]: {
          ...drawings[plateId],
          [wellNumber]: {
            ...drawings[plateId]?.[wellNumber],
            [dropNumber]: {
              ...drawings[plateId]?.[wellNumber]?.[dropNumber],
              drawingsDetails: {
                ...drawings[plateId]?.[wellNumber]?.[dropNumber].drawingsDetails,
                [regionName]: {
                  drawings: wellDrawings,
                  status: EFetchingStatus.Loaded,
                  canvasSize: getDrawingsFrame(tpObject),
                },
              },
              isImagingEnabled: imagingEnabled,
            },
          },
        },
      };
    });
    builder.addCase(updateDrawingThunk.fulfilled, (state, action) => {
      const {
        plateId,
        wellNumber,
        dropNumber,
        regionName,
        drawingType,
        drawingId,
        drawingUpdateDetails,
      } = action.meta.arg;
      WellSlice.caseReducers.updateDrawing(state, {
        type: 'updateDrawing',
        payload: {
          id: drawingId,
          drawingType,
          dropNumber,
          regionName,
          plateId,
          wellNumber,
          drawingUpdateDetails,
        },
      });
    });
    builder.addCase(fetchDropImageThunk.pending, (state, action) => {
      let drawings = state.wellDrawing;
      const { pid, wellNumber, dropNumber, batchId, cpID } = action.meta.arg;
      if (
        pid !== null &&
        wellNumber !== null &&
        dropNumber !== null &&
        batchId !== null &&
        cpID !== null
      ) {
        drawings = updateWellImage(
          drawings,
          pid,
          wellNumber,
          dropNumber,
          batchId,
          cpID,
          EFetchingStatus.InProgress,
          null,
        );
        state.wellDrawing = drawings;
      }
    });
    builder.addCase(fetchDropImageThunk.fulfilled, (state, action) => {
      const { pid, wellNumber, dropNumber, batchId, cpID } = action.meta.arg;
      let drawings = state.wellDrawing;
      const existingImage =
        drawings?.[pid]?.[wellNumber]?.[dropNumber]?.images?.[batchId]?.[cpID];

      if (existingImage) {
        existingImage.image = action.payload;
        existingImage.status = EFetchingStatus.Loaded;
      } else {
        drawings = updateWellImage(
          drawings,
          pid,
          wellNumber,
          dropNumber,
          batchId,
          cpID,
          action.payload ? EFetchingStatus.Loaded : EFetchingStatus.NotAvailable,
          action.payload,
        );
      }
      state.wellDrawing = drawings;
    });
    builder.addCase(saveDrawingThunk.fulfilled, (state, action) => {
      const {
        plateId,
        wellNumber,
        dropNumber,
        regionName,
        drawingType,
        drawingUpdateDetails,
      } = action.meta.arg;
      let detailsToUpdate = {} as DrawingUpdateDetails;
      let drawingId = action.payload;
      switch (drawingType) {
        case DrawingType.ROI:
          detailsToUpdate = {
            id: action.payload,
            imagingEnabled: true,
          } as RoiUpdateDetails;
          break;
        case DrawingType.Annotation:
          detailsToUpdate = {
            id: action.payload,
            text: (drawingUpdateDetails as AnnotationDrawing).text,
          } as AnnotationUpdateDetails;
          break;
        case DrawingType.CRYSTAL:
          detailsToUpdate = { ...action.payload } as CrystalDrawing;
          drawingId = action.payload.id;
          break;
        default:
          detailsToUpdate = { id: drawingId };
          break;
      }
      WellSlice.caseReducers.updateDrawing(state, {
        type: 'updateDrawing',
        payload: {
          id: drawingUpdateDetails.id,
          drawingType,
          dropNumber,
          plateId,
          wellNumber,
          regionName,
          drawingUpdateDetails: detailsToUpdate,
        },
      });

      WellSlice.caseReducers.setSelectedDrawing(state, {
        type: 'setSelectedDrawing',
        payload: {
          id: drawingId,
          drawingType,
          plateId,
          wellNumber,
          dropNumber,
          regionName,
        },
      });
    });
    builder.addCase(fetchDrawingPreferences.fulfilled, (state, action) => {
      const { fontPreferences, poiColor } = action.payload;
      WellSlice.caseReducers.setDrawingFontsPreferences(state, {
        type: 'setDrawingFontsPreferences',
        payload: fontPreferences,
      });
      state.poiColor = poiColor;
    });
    builder.addCase(
      deleteDrawing.fulfilled,
      (
        state,
        action: PayloadAction<{
          plateId: number;
          wellNumber: number;
          dropNumber: number;
          regionName: string;
          drawingType: DrawingType;
          id: number;
        }>,
      ) => {
        WellSlice.caseReducers.removeDrawing(state, {
          type: 'removeDrawing',
          payload: action.payload,
        });
        state.selectedDrawing = null;
      },
    );

    builder.addCase(
      toggleRoiImaging.fulfilled,
      (state, action: PayloadAction<DrawingUpdateRequestParams>) => {
        if (action.payload) {
          const {
            plateId,
            wellNumber,
            dropNumber,
            regionName,
            drawingType,
            drawingId,
            drawingUpdateDetails,
          } = action.payload;
          WellSlice.caseReducers.updateDrawing(state, {
            type: 'updateDrawing',
            payload: {
              id: drawingId,
              drawingType,
              dropNumber,
              plateId,
              wellNumber,
              regionName,
              drawingUpdateDetails,
            },
          });
        }
      },
    );
    builder.addCase(fetchImagingDetails.pending, (state, action) => {
      let drawings = state.wellDrawing;
      const { plateId, wellNumber, dropNumber, imageBatchId, cpId, regionId } =
        action.meta.arg;
      const imagingDetailsKey = getWellImagingDetailsKey(imageBatchId, cpId, regionId);
      drawings = {
        ...drawings,
        [plateId]: {
          ...drawings[plateId],
          [wellNumber]: {
            ...drawings[plateId]?.[wellNumber],
            [dropNumber]: {
              ...drawings[plateId]?.[wellNumber]?.[dropNumber],
              imagingDetails: {
                ...drawings[plateId]?.[wellNumber]?.[dropNumber]?.imagingDetails,
                [imagingDetailsKey]: { status: EFetchingStatus.InProgress },
              },
            },
          },
        },
      };
      state.wellDrawing = drawings;
    });

    builder.addCase(fetchImagingDetails.fulfilled, (state, action) => {
      let drawings = state.wellDrawing;
      const { plateId, wellNumber, dropNumber, imageBatchId, cpId, regionId } =
        action.meta.arg;
      const imagingDetailsKey = getWellImagingDetailsKey(imageBatchId, cpId, regionId);
      drawings = {
        ...drawings,
        [plateId]: {
          ...drawings[plateId],
          [wellNumber]: {
            ...drawings[plateId]?.[wellNumber],
            [dropNumber]: {
              ...drawings[plateId]?.[wellNumber]?.[dropNumber],
              imagingDetails: {
                ...drawings[plateId]?.[wellNumber]?.[dropNumber]?.imagingDetails,
                [imagingDetailsKey]: {
                  status: EFetchingStatus.Loaded,
                  ...action.payload,
                },
              },
            },
          },
        },
      };
      state.wellDrawing = drawings;
    });
    /**
     * Image Store case
     */
    //this case is being used for snf
    builder.addCase(fetchWellImagesBulkAction.fulfilled, (state, action) => {
      const list = {};
      action.payload.forEach((image) => {
        const imgStore: StoreImage = {
          fetchingStatus: EFetchingStatus.Loaded,
          key: image.key,
          blobUrl: '',
          meta: {},
        };
        list[image.key] = imgStore;
        setWellImageStoreImageByKey(image.key, image.wellImage);
        return imgStore;
      });

      state.imageStore = { ...state.imageStore, ...list };
    });
    builder.addCase(fetchWellDropImageMetaDataThunk.pending, (state, action) => {
      state.wellDropImageMetaData.status = EFetchingStatus.InProgress;
      state.wellDropImageMetaData.fetchedMetaDataInspectionIds[
        action.meta.arg.inspectionId
      ] = EFetchingStatus.InProgress;
    });
    builder.addCase(fetchWellDropImageMetaDataThunk.rejected, (state, action: any) => {
      state.wellDropImageMetaData.error =
        (action.payload?.ErrorMessage as string) || action.error.message;

      //the reason for the below condition is if we already have some data
      // and fetching more and it fails then we should put loaded state because we already have soem data.
      state.wellDropImageMetaData.status = isObjectEmpty(state.wellDropImageMetaData.data)
        ? EFetchingStatus.Error
        : EFetchingStatus.Loaded;

      state.wellDropImageMetaData.fetchedMetaDataInspectionIds[
        action.meta.arg.inspectionId
      ] = EFetchingStatus.Error;
    });
    builder.addCase(fetchWellDropImageMetaDataThunk.fulfilled, (state, action) => {
      const updatedData = { ...state.wellDropImageMetaData.data, ...action.payload };

      state.wellDropImageMetaData.fetchedMetaDataInspectionIds[
        action.meta.arg.inspectionId
      ] = EFetchingStatus.Loaded;
      state.wellDropImageMetaData.status = EFetchingStatus.Loaded;
      state.wellDropImageMetaData.data = updatedData;
    });
    builder.addCase(saveWellDropTuningValuesThunk.fulfilled, (state, action) => {
      const { uIKey, tuningValues } = action.payload;
      if (state.wellDropImageMetaData.data[uIKey]) {
        state.wellDropImageMetaData.data[uIKey].captureResultTuningValuesDto =
          tuningValues.toJSON();
      }
    });
  },
  reducers: {
    updateDrawing: (
      state,
      action: PayloadAction<{
        plateId: number;
        wellNumber: number;
        dropNumber: number;
        regionName: string;
        drawingType: DrawingType;
        id: number;
        drawingUpdateDetails: DrawingUpdateDetails;
      }>,
    ): void => {
      const {
        plateId,
        wellNumber,
        dropNumber,
        drawingType,
        id,
        drawingUpdateDetails,
        regionName,
      } = action.payload;
      const drawings =
        state.wellDrawing?.[plateId]?.[wellNumber]?.[dropNumber]?.drawingsDetails?.[
          regionName
        ]?.drawings?.[DrawingKey[drawingType]];

      if (drawings) {
        const drawingToUpdateIndex = getDrawingIndexById(drawings, id);
        if (drawingToUpdateIndex >= 0) {
          drawings[drawingToUpdateIndex] = {
            ...drawings[drawingToUpdateIndex],
            ...drawingUpdateDetails,
          };
        }
      }
    },
    updateMultipleDrawings: (
      state,
      action: PayloadAction<{
        plateId: number;
        wellNumber: number;
        dropNumber: number;
        regionName: string;
        drawingType: DrawingType;
        drawingUpdateDetails: DrawingUpdateDetails[];
      }>,
    ): void => {
      const {
        plateId,
        wellNumber,
        dropNumber,
        regionName,
        drawingType,
        drawingUpdateDetails,
      } = action.payload;

      const drawings =
        state.wellDrawing?.[plateId]?.[wellNumber]?.[dropNumber]?.drawingsDetails?.[
          regionName
        ]?.drawings?.[DrawingKey[drawingType]];

      if (drawings) {
        const updatedDrawings = drawings.map((drawing) => {
          const update = drawingUpdateDetails.find((update) => update.id === drawing.id);
          return update ? { ...drawing, ...update } : drawing;
        });

        state.wellDrawing[plateId][wellNumber][dropNumber].drawingsDetails[
          regionName
        ].drawings[DrawingKey[drawingType]] = updatedDrawings;
      }
    },

    addNewDrawing: (
      state,
      action: PayloadAction<{
        plateId: number;
        wellNumber: number;
        dropNumber: number;
        regionName: string;
        drawingType: DrawingType;
        drawingUpdateDetails: DrawingUpdateDetails;
      }>,
    ) => {
      const {
        plateId,
        wellNumber,
        dropNumber,
        regionName,
        drawingType,
        drawingUpdateDetails,
      } = action.payload;
      const allDrawings =
        state.wellDrawing?.[plateId]?.[wellNumber]?.[dropNumber]?.drawingsDetails?.[
          regionName
        ]?.drawings;
      const drawings = allDrawings?.[DrawingKey[drawingType]];
      let drawingToAdd = null;
      if (drawingType === DrawingType.POI) {
        drawingToAdd = drawingUpdateDetails as POIDrawing;
      } else if (drawingType === DrawingType.ROI) {
        drawingToAdd = drawingUpdateDetails as ROIDrawing;
      } else if (drawingType === DrawingType.Ruler) {
        drawingToAdd = drawingUpdateDetails as RulerDrawing;
      } else if (drawingType === DrawingType.Annotation) {
        drawingToAdd = drawingUpdateDetails as AnnotationDrawing;
      } else if (drawingType === DrawingType.Scribble) {
        drawingToAdd = drawingUpdateDetails as ScribbleDrawing;
      } else if (drawingType === DrawingType.CRYSTAL) {
        drawingToAdd = drawingUpdateDetails as CrystalDrawing;
      } else if (drawingType === DrawingType.DropLocation) {
        drawingToAdd = drawingUpdateDetails as DropLocationDrawing;
      }
      if (drawings) {
        drawings.push(drawingToAdd);
      } else {
        allDrawings[DrawingKey[drawingType]] = [drawingToAdd];
      }
    },
    removeDrawing: (
      state,
      action: PayloadAction<{
        plateId: number;
        wellNumber: number;
        dropNumber: number;
        regionName: string;
        drawingType: DrawingType;
        id: number;
      }>,
    ): void => {
      const { plateId, wellNumber, dropNumber, regionName, drawingType, id } =
        action.payload || {};
      const drawings =
        state.wellDrawing?.[plateId]?.[wellNumber]?.[dropNumber]?.drawingsDetails?.[
          regionName
        ]?.drawings?.[DrawingKey[drawingType]];

      if (drawings) {
        const drawingToDeleteIndex = getDrawingIndexById(drawings, id);
        if (drawingToDeleteIndex >= 0) {
          drawings.splice(drawingToDeleteIndex, 1);
        }
      }
    },

    removeDrawingsByIds: (
      state,
      action: PayloadAction<{
        plateId: number;
        wellNumber: number;
        dropNumber: number;
        regionName: string;
        drawingType: DrawingType;
        ids: number[];
      }>,
    ): void => {
      const { plateId, wellNumber, dropNumber, regionName, drawingType, ids } =
        action.payload;
      const drawings =
        state.wellDrawing?.[plateId]?.[wellNumber]?.[dropNumber].drawingsDetails?.[
          regionName
        ]?.drawings?.[DrawingKey[drawingType]];

      if (drawings) {
        ids.forEach((id) => {
          const drawingToDeleteIndex = getDrawingIndexById(drawings, id);
          if (drawingToDeleteIndex >= 0) {
            drawings.splice(drawingToDeleteIndex, 1);
          }
        });
      }
    },
    updateTuningValues: (
      state,
      action: PayloadAction<{
        plateId: number;
        wellNumber: number;
        dropNumber: number;
        imageBatchId: number;
        cpId: number;
        lowerLimit: number;
        upperLimit: number;
        autoLevelingOn?: boolean;
      }>,
    ): void => {
      const {
        plateId,
        wellNumber,
        dropNumber,
        imageBatchId,
        cpId,
        lowerLimit,
        upperLimit,
        autoLevelingOn = false,
      } = action.payload;
      const drawings = state.wellDrawing;
      const existingImage =
        drawings?.[plateId]?.[wellNumber]?.[dropNumber]?.images?.[imageBatchId]?.[cpId]
          ?.image;
      const updatedImage = existingImage
        ? {
            ...existingImage,
            captureResultTuningValuesDto: {
              ...existingImage.captureResultTuningValuesDto,
              lowerLevelLimit: lowerLimit,
              upperLevelLimit: upperLimit,
              isAutoLevelingOn: autoLevelingOn,
            },
          }
        : ({} as IWellImageDTO);
      state.wellDrawing[plateId][wellNumber][dropNumber].images[imageBatchId][
        cpId
      ].image = updatedImage;
    },
    setSelectedDrawing: (
      state,
      action: PayloadAction<{
        plateId: number;
        wellNumber: number;
        dropNumber: number;
        regionName: string;
        drawingType: DrawingType;
        id: number;
      }>,
    ) => {
      const { plateId, wellNumber, dropNumber, regionName, drawingType, id } =
        action.payload;
      const drawings =
        state.wellDrawing?.[plateId]?.[wellNumber]?.[dropNumber]?.drawingsDetails?.[
          regionName
        ]?.drawings?.[DrawingKey[drawingType]];
      if (drawings) {
        state.selectedDrawing = {
          drawingId: id,
          drawingType,
          plateId,
          wellNumber,
          dropNumber,
          regionName,
        };
      }
      WellSlice.caseReducers.moveDrawingToFront(state, {
        type: 'moveDrawingToFront',
        payload: {
          drawingId: id,
          drawingType,
          plateId,
          dropNumber,
          regionName,
          wellNumber,
        },
      });
    },
    unSelectDrawing: (state) => {
      state.selectedDrawing = null;
    },
    setDrawingTool: (state, action: PayloadAction<OverlayTools>) => {
      state.selectedDrawingTool = action.payload;
      state.selectedDrawing = null;
    },
    setDrawingFontsPreferences: (
      state,
      action: PayloadAction<DrawingFontsPreference>,
    ) => {
      state.drawingFontsPreferences = action.payload;
    },
    setDrawingColor: (state, action: PayloadAction<RGBColor | string>) => {
      state.drawingColor = action.payload;
    },

    adjustDrawingsPositions: (
      state,
      action: PayloadAction<{
        plateId: number;
        wellNumber: number;
        dropNumber: number;
        regionName: string;
        tpObject: TransformPoints;
      }>,
    ) => {
      const { plateId, wellNumber, dropNumber, regionName, tpObject } = action.payload;
      const drawings = state.wellDrawing;
      const currentDrawingsData: {
        drawings: DrawingsData;
        canvasSize: DrawingsFrame;
        status: EFetchingStatus;
      } = drawings?.[plateId]?.[wellNumber]?.[dropNumber]?.drawingsDetails?.[regionName];
      if (
        currentDrawingsData?.canvasSize &&
        isDrawingsFrameStale(currentDrawingsData.canvasSize, tpObject)
      ) {
        const updatedDrawings = Object.entries(currentDrawingsData?.drawings).reduce(
          (acc, [key, value]) => {
            if (
              key === DrawingKey[DrawingType.ROI] ||
              key === DrawingKey[DrawingType.DropLocation]
            ) {
              acc[key] = value.map((drawing) => {
                const { xCenter, yCenter, width, height } = tpObject.ConvertROIToCanvas({
                  xWidth: drawing.dbWidth,
                  yHeight: drawing.dbHeight,
                  xCenterOffSet: drawing.dbXCenterOffset,
                  yCenterOffSet: drawing.dbYCenterOffset,
                });
                return { ...drawing, x: xCenter, y: yCenter, width, height };
              });
            } else if (key === DrawingKey[DrawingType.CRYSTAL]) {
              acc[key] = value.map((crystal) => {
                const { xCenter, yCenter, width, height } =
                  tpObject?.ConvertRectanglePointsToCanvas(
                    crystal.dbX,
                    crystal.dbY,
                    crystal.dbWidth,
                    crystal.dbHeight,
                  );
                return { ...crystal, x: xCenter, y: yCenter, height, width };
              });
            } else if (key === DrawingKey[DrawingType.Ruler]) {
              acc[key] = value.map((ruler) => {
                const [x1, y1] = tpObject.FromDbToCanvas(ruler.dbX1, ruler.dbY1);
                const [x2, y2] = tpObject.FromDbToCanvas(ruler.dbX2, ruler.dbY2);
                return { ...ruler, x1, y1, x2, y2 };
              });
            } else {
              acc[key] = value.map((item) => {
                const [newX, newY] = tpObject.FromDbToCanvas(item.dbX, item.dbY);
                return { ...item, x: newX, y: newY };
              });
            }
            return acc;
          },
          {},
        );

        state.wellDrawing[plateId][wellNumber][dropNumber].drawingsDetails[
          regionName
        ].drawings = updatedDrawings as DrawingsData;
        state.wellDrawing[plateId][wellNumber][dropNumber].drawingsDetails[
          regionName
        ].canvasSize = getDrawingsFrame(tpObject);
      }
    },
    setSelectedWellDropDetailsIndex: (state, action: PayloadAction<number>) => {
      state.selectedWellDropDetailsIndex = action.payload;
    },
    setShowOverrideTuning: (state, action: PayloadAction<boolean>) => {
      state.showOverrideTuning = action.payload;
    },
    setDropLocation: (
      state,
      action: PayloadAction<{
        plateId: number;
        wellNumber: number;
        dropNumber: number;
        regionName: string;
        drawing: DropLocationDrawing;
      }>,
    ): void => {
      const { plateId, wellNumber, dropNumber, regionName, drawing } = action.payload;
      if (
        state.wellDrawing[plateId]?.[wellNumber]?.[dropNumber]?.drawingsDetails[
          regionName
        ]?.drawings
      ) {
        state.wellDrawing[plateId][wellNumber][dropNumber].drawingsDetails[
          regionName
        ].drawings.dropLocations = [drawing];
      }
    },
    removeDropLocation: (
      state,
      action: PayloadAction<{
        plateId: number;
        wellNumber: number;
        dropNumber: number;
        regionName: string;
      }>,
    ): void => {
      const { plateId, wellNumber, dropNumber, regionName } = action.payload;

      if (
        state.wellDrawing[plateId]?.[wellNumber]?.[dropNumber]?.drawingsDetails[
          regionName
        ]?.drawings
      ) {
        state.wellDrawing[plateId][wellNumber][dropNumber].drawingsDetails[
          regionName
        ].drawings.dropLocations = [];
      }
    },
    updateCrystalLock: (
      state,
      action: PayloadAction<{
        plateId: number;
        wellNumber: number;
        dropNumber: number;
        regionName: string;
        drawingType: DrawingType;
        id: number;
        locked: boolean;
      }>,
    ): void => {
      const { plateId, wellNumber, dropNumber, regionName, drawingType, id, locked } =
        action.payload;
      const drawings =
        state.wellDrawing?.[plateId]?.[wellNumber]?.[dropNumber]?.drawingsDetails?.[
          regionName
        ].drawings?.[DrawingKey[drawingType]];

      if (drawings) {
        const drawingToUpdateIndex = getDrawingIndexById(drawings, id);
        if (drawingToUpdateIndex >= 0) {
          drawings[drawingToUpdateIndex] = {
            ...drawings[drawingToUpdateIndex],
            locked: locked,
          };
        }
      }
    },
    setShowFrapGraph: (state, action: PayloadAction<boolean>) => {
      state.showFrapGraph = action.payload;
    },

    appendFocusLevelImagesAvailableKeys: (state, action: PayloadAction<string>) => {
      state.FocusLevelImagesAvailableKeys.push(action.payload);
    },
    removeFocusLevelImagesAvailableKey: (
      state,
      action: PayloadAction<{ key: string }>,
    ) => {
      const { key } = action.payload;
      state.FocusLevelImagesAvailableKeys = state.FocusLevelImagesAvailableKeys.filter(
        (k) => k !== key,
      );
    },
    toggleWellIndependentZoom: (state, action: PayloadAction<string>) => {
      const key = action.payload;
      state.wellsIndependentZoom[key] = !state.wellsIndependentZoom[key];
    },

    moveDrawing: (
      state,
      action: PayloadAction<{
        plateId: number;
        wellNumber: number;
        dropNumber: number;
        regionName: string;
        drawingType: DrawingType;
        drawingId: number;
        direction: 'front' | 'back';
      }>,
    ) => {
      const {
        plateId,
        drawingId,
        drawingType,
        dropNumber,
        regionName,
        wellNumber,
        direction,
      } = action.payload;
      const allDrawings: DrawingsData =
        state.wellDrawing?.[plateId]?.[wellNumber]?.[dropNumber]?.drawingsDetails?.[
          regionName
        ].drawings;

      const newZIndex =
        direction === 'front'
          ? getHighestZIndex(allDrawings) + 1
          : getLowestZIndex(allDrawings) - 1;

      WellSlice.caseReducers.updateDrawing(state, {
        type: 'updateDrawing',
        payload: {
          id: drawingId,
          drawingType,
          plateId,
          wellNumber,
          dropNumber,
          regionName,
          drawingUpdateDetails: { zIndex: newZIndex },
        },
      });
    },
    moveDrawingToBack: (
      state,
      action: PayloadAction<{
        plateId: number;
        wellNumber: number;
        dropNumber: number;
        regionName: string;
        drawingType: DrawingType;
        drawingId: number;
      }>,
    ) => {
      WellSlice.caseReducers.moveDrawing(state, {
        type: 'moveDrawing',
        payload: { ...action.payload, direction: 'back' },
      });
    },
    moveDrawingToFront: (
      state,
      action: PayloadAction<{
        plateId: number;
        wellNumber: number;
        dropNumber: number;
        regionName: string;
        drawingType: DrawingType;
        drawingId: number;
      }>,
    ) => {
      WellSlice.caseReducers.moveDrawing(state, {
        type: 'moveDrawing',
        payload: { ...action.payload, direction: 'front' },
      });
    },
    toggleScaleBarVisibility: (state) => {
      state.showScaleBar = !state.showScaleBar;
    },
    toggleOverlaysVisibility: (state) => {
      state.showOverlays = !state.showOverlays;
    },
    toggleWellDropInfoPanelVisibility: (state) => {
      state.showWellDropInfoPanel = !state.showWellDropInfoPanel;
    },
    setIsCreatingDrawing: (state, action: PayloadAction<boolean>) => {
      state.isCreatingDrawing = action.payload;
    },
    setImageZoomDto: (state, action: PayloadAction<IImageZoomDTO>) => {
      state.imageZoomDto = action.payload;
    },
    setWellDropImageStatus: (
      state,
      action: PayloadAction<{
        key: string;
        status: EFetchingStatus;
      }>,
    ) => {
      const { key, status } = action.payload;
      state.imageStore[key] = {
        key,
        blobUrl: null,
        fetchingStatus: status,
        meta: {},
      };
    },
    removeWellDropImage: (state, action: PayloadAction<{ key: string }>) => {
      const { key } = action.payload;
      if (state.imageStore[key]) {
        delete state.imageStore[key];
      }
    },
    setWellDropImageFullFilled: (
      state,
      action: PayloadAction<{
        key: string;
        imageOrg: HTMLImageElement;
        imageTuned: HTMLImageElement;
        updatedTuningValues?: ImageTuningValues;
        plateTuningValues?: ImageTuningValues;
        originalByteSize?: number;
        tunedByteSize?: number;
      }>,
    ) => {
      const {
        key,
        imageOrg,
        imageTuned,
        updatedTuningValues,
        plateTuningValues,
        originalByteSize,
        tunedByteSize,
      } = action.payload;
      if (!imageOrg) {
        return;
      }
      WellDropImageStore.set(key, {
        original: imageOrg,
        tuned: imageTuned || null,
        tuningValues: updatedTuningValues ?? plateTuningValues,
        originalByteSize,
        tunedByteSize,
      });

      state.imageStore[key] = {
        key,
        blobUrl: null,
        fetchingStatus: EFetchingStatus.Loaded,
        meta: {
          updatedTuningValues: updatedTuningValues || null,
        },
      };
    },
    updateWellDropImageTuningValues: (
      state,
      action: PayloadAction<{
        key: string;
        updatedTuningValues: ImageTuningValues;
      }>,
    ) => {
      const { key } = action.payload;
      if (state.imageStore[key]) {
        state.imageStore[key].meta = {
          updatedTuningValues: action.payload.updatedTuningValues || null,
        };
      }
    },
    updateWellDropImageBFLDetails: (
      state,
      action: PayloadAction<{
        key: string;
        imageDetails: TFocusLevelImage;
      }>,
    ) => {
      const { key, imageDetails } = action.payload;
      if (state.wellDropImageMetaData.data[key]) {
        state.wellDropImageMetaData.data[key] = {
          ...state.wellDropImageMetaData.data[key],
          bflImageTypeIndex: imageDetails.imageTypeIndex,
          bestFlImageName: imageDetails.imageTitle,
          pixelSize: imageDetails.pixelSize,
          imageTypeShortName: imageDetails.imageTypeShortName,
        };
      }
    },
    setImageLoadingStarted: (state, action: PayloadAction<IWellImageParams[]>) => {
      const list = {};
      action.payload.forEach((params) => {
        const wellImageUniqueKey = params.key || getWellDropImageUniqueKey(params);
        const imgStore: StoreImage = {
          fetchingStatus: EFetchingStatus.InProgress,
          key: wellImageUniqueKey,
          blobUrl: null,
          meta: {},
        };
        list[wellImageUniqueKey] = imgStore;
        return imgStore;
      });
      state.imageStore = { ...state.imageStore, ...list };
    },
    setWellDropCompareMode: (state, action: PayloadAction<ECompareMode>) => {
      state.wellDropCompare.compareMode = action.payload;
    },
    setWellDropCompareOverlayOpacity: (state, action: PayloadAction<number>) => {
      state.wellDropCompare.overlayOpacity = action.payload;
    },
    setWellDropCompareOpacityBar: (state, action: PayloadAction<boolean>) => {
      state.wellDropCompare.displayOpacityBar = action.payload;
    },
    toggleWellDropCompareLockPan: (state) => {
      state.wellDropCompare.lockPan = !state.wellDropCompare.lockPan;
    },
  },
});

export const {
  updateDrawing,
  updateMultipleDrawings,
  addNewDrawing,
  removeDrawing,
  removeDrawingsByIds,
  setSelectedDrawing,
  unSelectDrawing,
  updateTuningValues,
  setDrawingTool,
  setDrawingFontsPreferences,
  setDrawingColor,
  adjustDrawingsPositions,
  setSelectedWellDropDetailsIndex,
  setShowOverrideTuning,
  setDropLocation,
  removeDropLocation,
  updateCrystalLock,
  setShowFrapGraph,
  appendFocusLevelImagesAvailableKeys,
  removeFocusLevelImagesAvailableKey,
  toggleWellIndependentZoom,
  moveDrawingToBack,
  moveDrawingToFront,
  toggleScaleBarVisibility,
  toggleOverlaysVisibility,
  toggleWellDropInfoPanelVisibility,
  setIsCreatingDrawing,
  setImageZoomDto,
  setWellDropImageStatus,
  removeWellDropImage,
  setWellDropImageFullFilled,
  updateWellDropImageTuningValues,
  updateWellDropImageBFLDetails,
  setImageLoadingStarted,
  setWellDropCompareMode,
  setWellDropCompareOverlayOpacity,
  setWellDropCompareOpacityBar,
  toggleWellDropCompareLockPan,
} = WellSlice.actions;

export default WellSlice.reducer;

const getDrawingIndexById = (drawings, id): number => {
  return drawings.findIndex((item) => item.id === id);
};

const wellImagesStoreInMemory = {};
/**
 *
 * @param key combination of plateId, wellNumber, dropNumber, batchId, cpID, regionId  - ref:getWellDropImageUniqueKey() function
 * @returns
 */
export const getWellImageStoreImageByKey = (key: string) => {
  return wellImagesStoreInMemory[key];
};

export const getWellImageStoreObject = () => {
  return wellImagesStoreInMemory;
};

/**
 * @param key key combine of plateId, wellNumber, dropNumber, batchId, cpID  - ref:getWellDropImageUniqueKey() function
 * @param image either base64 string or HTMLImageElement
 * (base64 is used for snf, which we will remove in future and keep the HTMLImageElement for performance)
 */
export const setWellImageStoreImageByKey = (
  key: string,
  image: string | HTMLImageElement,
) => {
  wellImagesStoreInMemory[key] = image;
};

const updateWellImage = (
  drawings: DropData,
  pid: number,
  wellNumber: number,
  dropNumber: number,
  batchId: number,
  cpID: number,
  status: EFetchingStatus,
  wellImage: IWellImageDTO,
) => {
  drawings = {
    ...drawings,
    [pid]: {
      ...(drawings?.[pid] || {}),
      [wellNumber]: {
        ...(drawings?.[pid]?.[wellNumber] || {}),
        [dropNumber]: {
          ...(drawings?.[pid]?.[wellNumber]?.[dropNumber] || {}),
          images: {
            ...(drawings?.[pid]?.[wellNumber]?.[dropNumber]?.images || {}),
            [batchId]: {
              ...(drawings?.[pid]?.[wellNumber]?.[dropNumber]?.images?.[batchId] || {}),
              [cpID]: {
                image: wellImage,
                status: status,
              },
            },
          },
        },
      },
    },
  } as DropData;
  return drawings;
};

const getLowestZIndex = (drawings: DrawingsData): number =>
  Math.min(
    ...Object.values(drawings)
      .flat()
      .map((item) => item.zIndex),
  );

const getHighestZIndex = (drawings: DrawingsData): number =>
  Math.max(
    ...Object.values(drawings)
      .flat()
      .map((item) => item.zIndex),
  );
